import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ViewToggle from './ViewToggle';
import { combine, windowsAvailable } from './sections';
import { formatPct } from './timeframes';
import { useT } from '../../i18n';

// Starting Value, Current Value and P/L for a section, over a lookback window.
// See sections.js for what each figure means and which windows are offered.
//
// Where a Starting Value has to be typed in — the crypto wallets, an account
// whose history starts after its money arrived — it is marked as needing input
// and edited in place. Built from plain divs: the Qode theme styles bare
// `header`/`footer` globally.

/** "+$1,234" / "−$1,234" / "—". */
export function useMoney() {
    const { fmt } = useT();
    const money = (v) => (v === null || v === undefined ? '—' : `${v < 0 ? '−' : ''}$${fmt.number(Math.abs(v), 0)}`);
    const signed = (v) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}$${fmt.number(Math.abs(v), 0)}`);
    return { money, signed };
}

const tone = (v) => (v === null || v === undefined || v === 0 ? '' : v < 0 ? ' is-negative' : ' is-positive');

/** A Starting Value typed in by hand, with its own save and cancel. */
function StartInput({ value, onSave, onCancel }) {
    const { t } = useT();
    const [draft, setDraft] = useState(value === null || value === undefined ? '' : String(value));
    const [busy, setBusy] = useState(false);
    const [bad, setBad] = useState(false);

    const save = async () => {
        const text = draft.replace(/[$,\s]/g, '');
        const number = text === '' ? null : Number(text);
        if (number !== null && !Number.isFinite(number)) { setBad(true); return; }
        setBusy(true);
        const ok = await onSave(number);
        setBusy(false);
        if (!ok) setBad(true);
    };

    return (
        <div className="ssum__edit">
            <input
                className={`private__input is-right${bad ? ' is-invalid' : ''}`}
                inputMode="decimal"
                value={draft}
                onChange={(e) => { setDraft(e.target.value); setBad(false); }}
                onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onCancel(); }}
                placeholder={t('private.usdPlaceholder')}
                aria-label={t('sections.start')}
                autoFocus
            />
            <div className="ssum__edit_actions">
                <button type="button" className="notes__link" onClick={save} disabled={busy}>{t('common.save')}</button>
                <button type="button" className="notes__link" onClick={onCancel} disabled={busy}>{t('common.cancel')}</button>
            </div>
        </div>
    );
}

/** The Starting Value cell: the figure, or a "needs input" marker, and the
 *  pencil when it can be typed in. */
function Start({ figures, inputKey, inputs, onInput, editPath, large }) {
    const { t } = useT();
    const { money, signed } = useMoney();
    const [editing, setEditing] = useState(false);
    const editable = Boolean(inputKey && onInput);

    if (editing) {
        return (
            <StartInput
                value={inputs?.[inputKey] ?? figures?.start ?? null}
                onCancel={() => setEditing(false)}
                onSave={async (v) => {
                    const ok = await onInput(inputKey, v);
                    if (ok) setEditing(false);
                    return ok;
                }}
            />
        );
    }
    const missing = figures?.start === null || figures?.start === undefined;
    return (
        <span className={large ? 'perf__value' : 'ssum__cell'}>
            {!missing && money(figures.start)}
            {missing && editable && (
                <button type="button" className="ssum__needs ssum__needs_btn" onClick={() => setEditing(true)}>
                    {t('sections.needsInput')}
                </button>
            )}
            {missing && !editable && editPath && (
                <Link to={editPath} className="ssum__needs ssum__needs_btn">{t('sections.needsInput')}</Link>
            )}
            {missing && !editable && !editPath && <span className="ssum__needs">{t('sections.needsInput')}</span>}
            {editable && (
                <button
                    type="button"
                    className="ssum__pencil"
                    onClick={() => setEditing(true)}
                    aria-label={t('sections.editStart')}
                    title={t('sections.editStart')}
                >
                    ✎
                </button>
            )}
            {!missing && figures.deposits ? (
                <span className="perf__note">{t('sections.inclDeposits', { amount: signed(figures.deposits) })}</span>
            ) : null}
        </span>
    );
}

export default function SectionSummary({
    parts, windowKey, onWindow, inputs, onInput, showParts = false, title = null,
}) {
    const { t, tn, fmt } = useT();
    const { money, signed } = useMoney();
    const available = windowsAvailable(parts);
    if (available.length === 0) return null;
    const key = available.some((w) => w.key === windowKey) ? windowKey : available.at(-1).key;
    const figures = combine(parts, key);
    const single = parts.length === 1 ? parts[0] : null;
    // Typed-in values are for All only: a rolling window moves under them.
    const inputFor = (part) => (key === 'all' && part?.inputKey ? part.inputKey : null);
    const pct = formatPct(figures?.pnlPct, 1);

    return (
        <div className="perf ssum">
            {(title || available.length > 1) && (
                <div className="ssum__top">
                    {title && <h2 className="notes__h ssum__title">{title}</h2>}
                    {available.length > 1 && (
                        <ViewToggle
                            ariaLabel={t('timeframe.aria')}
                            options={available.map((w) => [w.key, t(w.labelKey)])}
                            value={key}
                            onChange={onWindow}
                        />
                    )}
                </div>
            )}
            <div className="perf__head">
                <div>
                    <span className="perf__label">{t('sections.start')}</span>
                    <Start figures={figures} inputKey={inputFor(single)} inputs={inputs} onInput={onInput} editPath={single?.editPath} large />
                </div>
                <div>
                    <span className="perf__label">{t('sections.current')}</span>
                    <span className="perf__value">{money(figures?.current ?? null)}</span>
                </div>
                <div>
                    <span className="perf__label">{t('sections.pnl')}</span>
                    <span className={`perf__value${tone(figures?.pnl)}`}>
                        {signed(figures?.pnl ?? null)}
                        {pct && <span className="perf__row_pct">{pct}</span>}
                    </span>
                    {figures?.needsInput > 0 && !single && (
                        <span className="perf__note">{tn('sections.partsNeedInput', figures.needsInput)}</span>
                    )}
                    {/* Without the parts table, each missing figure links to
                        where it is entered. */}
                    {figures?.needsInput > 0 && !single && !showParts && (
                        <span className="perf__note ssum__missing">
                            {parts.filter((p) => p.windows[key]?.start === null && p.editPath).map((p) => (
                                <Link key={p.key} to={p.editPath} className="ssum__needs ssum__needs_btn">
                                    {p.labelKey ? t(p.labelKey) : p.label}
                                </Link>
                            ))}
                        </span>
                    )}
                </div>
            </div>
            {figures?.fromDate && (
                <p className="perf__period">{t('sections.since', { date: fmt.date(figures.fromDate) })}</p>
            )}

            {showParts && parts.length > 1 && (
                <div className="positions__scroll">
                    <table className="positions ssum__parts">
                        <thead>
                            <tr>
                                <th className="is-left"><span>{t('sections.part')}</span></th>
                                <th className="is-right"><span>{t('sections.start')}</span></th>
                                <th className="is-right"><span>{t('sections.current')}</span></th>
                                <th className="is-right"><span>{t('sections.pnl')}</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {parts.map((part) => {
                                const f = combine([part], key);
                                return (
                                    <tr key={part.key}>
                                        <td className="is-left">{part.labelKey ? t(part.labelKey) : part.label}</td>
                                        <td className="is-right">
                                            <Start figures={f} inputKey={inputFor(part)} inputs={inputs} onInput={onInput} editPath={part.editPath} />
                                        </td>
                                        <td className="is-right">{money(f?.current ?? null)}</td>
                                        <td className={`is-right${tone(f?.pnl)}`}>
                                            {signed(f?.pnl ?? null)}
                                            {formatPct(f?.pnlPct, 1) && <span className="private__pct">{formatPct(f.pnlPct, 1)}</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/** The overview's four boxes, each a link to its tab. */
export function SectionCards({ sections }) {
    const { t } = useT();
    const { money, signed } = useMoney();
    return (
        <div className="acct__grid">
            {sections.map(({ section, figures }) => (
                <Link key={section.key} to={section.path} className="acct ssum__card">
                    <div className="acct__head">
                        <h4 className="acct__name">{t(section.labelKey)}</h4>
                    </div>
                    <div className="acct__value">
                        <span className="acct__value_amount">{money(figures?.current ?? null)}</span>
                    </div>
                    <ul className="acct__figures">
                        <li className="acct__figure">
                            <span className="acct__figure_label">{t('sections.start')}</span>
                            <span className="acct__figure_value">
                                {figures?.start === null || figures?.start === undefined
                                    ? <span className="ssum__needs">{t('sections.needsInput')}</span>
                                    : money(figures.start)}
                            </span>
                        </li>
                        <li className="acct__figure">
                            <span className="acct__figure_label">{t('sections.pnl')}</span>
                            <span className={`acct__figure_value${tone(figures?.pnl)}`}>
                                {signed(figures?.pnl ?? null)}
                                {formatPct(figures?.pnlPct, 1) && (
                                    <span className="acct__figure_pct">{formatPct(figures.pnlPct, 1)}</span>
                                )}
                            </span>
                        </li>
                    </ul>
                </Link>
            ))}
        </div>
    );
}
